import * as CryptoJS from 'crypto-js';
import jwt_decode from 'jwt-decode';
import aconexApi from '../api/AconexApi';
import configuration from '../configs/IConfiguration';
import { AconexOauthResponse } from '../model/entities';
import cookieService from './CookieService';
import mioAdminSettingService from './MioAdminSettingService';

class AconexService {
    private clientId: string | undefined;
    private readonly urlStart = window.location.protocol + '//' + window.location.host;
    private readonly redirectUrl = `${this.urlStart}/aconex/oauth`;
    private readonly aconexClientIdText = 'aconexClientId';
    private readonly aconexStateCode = 'aconexStateCode';
    private readonly aconexAccessTokenTextOne = 'aconexAccessTokenOne';
    private readonly aconexAccessTokenTextTwo = 'aconexAccessTokenTwo';
    private readonly aconexAccessRefreshTokenText = 'aconexAccessRefreshToken';
    private readonly aconexWindow = 'aconexWindow';
    private readonly aconexCodeVerifier = 'aconexCodeVerifier';

    /**
     * Initializes the aconex access token.
     */
    init = async (): Promise<string | undefined> => {
        let accessToken: string | undefined;
        try {
            // initialize the aconex client id by organization
            this.initClientId();

            // get aconex authorization code (only when it comes from aconex authorization redirection) and the state code
            const authorizationCode = await this.getAuthorizationCode();
            const stateCode = await this.getStateCode();
            accessToken = this.getCachedAccessToken();

            // if authorization code is received, retrieve access and refresh tokens from aconex (store in cookies)
            if (authorizationCode && stateCode === localStorage.getItem(this.aconexStateCode)) {
                accessToken = await this.loadAccessTokenFromAuthorizationCode(authorizationCode);
                localStorage.removeItem(this.aconexStateCode);
            }

            // if access token exists but it's not valid, try to refresh it
            if (accessToken && !this.isAccessTokenValid(accessToken)) {
                accessToken = await this.refreshAccessToken();
            }
            // otherwise the access token is valid

            // close aconex login popup if is opened
            if (localStorage.getItem(this.aconexWindow)) {
                window.close();
                localStorage.removeItem(this.aconexWindow);
            }
        } catch (error: any) {
            accessToken = undefined;
            this.removeAccessToken();
            this.removeRefreshAccessToken();
            console.log(error);
        }

        return accessToken;
    };

    /**
     * Returns the aconex access token (from cache or via refresh access token)
     * @returns aconex access token
     */
    getAccessToken = async (): Promise<string | undefined> => {
        let accessToken: string | undefined;
        try {
            accessToken = this.getCachedAccessToken();

            if (accessToken && !this.isAccessTokenValid(accessToken)) {
                accessToken = await this.refreshAccessToken();
            }
        } catch (error: any) {
            this.removeAccessToken();
            this.removeRefreshAccessToken();
            console.log(error);
        }

        return accessToken;
    };

    /**
     * Returns the aconex access token (from cache or via refresh access token, otherwise redirects to login)
     * @returns aconex access token
     */
    getAccessTokenOrLogin = async (): Promise<string | undefined> => {
        let accessToken: string | undefined;
        try {
            accessToken = this.getCachedAccessToken();

            if (accessToken && !this.isAccessTokenValid(accessToken)) {
                accessToken = await this.refreshAccessToken();
            }
            // if access token doesn't exist redirect to aconex authorization page
            else if (!accessToken) {
                this.login();
            }
            // otherwise the access token is valid
        } catch (error: any) {
            accessToken = undefined;
            this.removeAccessToken();
            this.removeRefreshAccessToken();
            throw error;
        }

        return accessToken;
    };

    logOut = () => {
        this.removeClientId();
        this.removeAccessToken();
        this.removeRefreshAccessToken();
        localStorage.removeItem(this.aconexStateCode);
        localStorage.removeItem(this.aconexWindow);
        sessionStorage.removeItem(this.aconexCodeVerifier);
    };

    /**
     * Return if user is connected to Aconex
     * @returns a boolean to know if the user is or not connected
     */
    isConnected = (): boolean => {
        const aconexAccessToken = this.getCachedAccessToken();
        return !!aconexAccessToken && this.isAccessTokenValid(aconexAccessToken);
    };

    /**
     * Returns the aconex client id from the cookie
     * @returns the aconex client id from the cookie
     */
    getCachedClientId = (): string | undefined => {
        return cookieService.getCookie(this.aconexClientIdText);
    };

    /**
     * Returns the aconex access token.
     * @returns the aconex access token
     */
    getCachedAccessToken = (): string | undefined => {
        const accessTokenOne = cookieService.getCookie(this.aconexAccessTokenTextOne);
        const accessTokenTwo = cookieService.getCookie(this.aconexAccessTokenTextTwo);

        let accessToken;
        if (!!accessTokenOne && !!accessTokenTwo) {
            accessToken = accessTokenOne + accessTokenTwo;
        }

        return accessToken;
    };

    /**
     * Returns the aconex refresh access token.
     * @returns the aconex refresh access token
     */
    getCachedRefreshAccessToken = (): string | undefined => {
        return cookieService.getCookie(this.aconexAccessRefreshTokenText);
    };

    /**
     * Initializes the aconex client id (property, cookie) using mio admin settings or the default one.
     * @returns the aconex client id
     */
    private initClientId = () => {
        const aconexSetting = mioAdminSettingService.getAconexSetting();

        // load client id
        if (aconexSetting) {
            this.clientId = aconexSetting.publicClientId;

            // if new orgnization, reset client id and tokens
            if (this.clientId !== this.getCachedClientId()) {
                this.logOut();
                this.storeClientId(aconexSetting.publicClientId);
            }
        } else {
            this.clientId = undefined;
            this.logOut();
        }
    };

    private login = async () => {
        this.removeAccessToken();
        this.removeRefreshAccessToken();

        // redirect to aconex authorization page
        const aconexSetting = mioAdminSettingService.getAconexSetting();
        if (aconexSetting) {
            // we generate the code challenge for PKCE auth
            const characterArray = CryptoJS.lib.WordArray.random(34).toString();
            const str = CryptoJS.enc.Utf8.parse(characterArray);
            const codeVerifier = CryptoJS.enc.Base64.stringify(str)
                .replace(/\+/g, '-')
                .replace(/\//g, '_')
                .replace(/=/g, '');
            const hash = CryptoJS.SHA256(codeVerifier);
            const codeChallenge = CryptoJS.enc.Base64.stringify(hash)
                .replace(/\+/g, '-')
                .replace(/\//g, '_')
                .replace(/=/g, '');

            const stateCode = CryptoJS.lib.WordArray.random(34).toString();
            // we generate the url for aconex authentication
            const url = new URL(`${configuration.getAconexUrl()}/auth/authorize`);
            url.searchParams.set('response_type', 'code');
            url.searchParams.set('client_id', aconexSetting.publicClientId);
            url.searchParams.set('redirect_uri', this.redirectUrl);
            url.searchParams.set('code_challenge', codeChallenge);
            url.searchParams.set('code_challenge_method', 'S256');
            url.searchParams.set('state', stateCode);

            // store code verifier in local storage
            sessionStorage.setItem(this.aconexCodeVerifier, codeVerifier);
            // store state code in local storage
            localStorage.setItem(this.aconexStateCode, stateCode);

            // we create the aconex login popup
            const width = 600,
                height = 700;
            const left = (window.innerWidth - width) / 2;
            const top = (window.innerHeight - height) / 2;

            localStorage.setItem(this.aconexWindow, 'Aconex Login');

            window.open(url, 'Aconex Login', `width=${width},height=${height},top=${top},left=${left}`);
        }
    };

    private getAuthorizationCode = async () => {
        let code: string | undefined;
        try {
            const urlObj = new URL(window.location.href);
            if (window.location.pathname === '/aconex/oauth' && urlObj.searchParams.has('code') && this.clientId) {
                const urlParams = new URLSearchParams(window.location.search);
                code = urlParams.get('code') as string;
            }
        } catch (error) {
            console.log(error);
        }

        return code;
    };

    private getStateCode = async () => {
        let state: string | undefined;
        try {
            const urlObj = new URL(window.location.href);
            if (window.location.pathname === '/aconex/oauth' && urlObj.searchParams.has('state') && this.clientId) {
                const urlParams = new URLSearchParams(window.location.search);
                state = urlParams.get('state') as string;
            }
        } catch (error) {
            console.log(error);
        }

        return state;
    };

    private loadAccessTokenFromAuthorizationCode = async (authorizationCode: string): Promise<string | undefined> => {
        let accessToken: string | undefined;
        try {
            const codeVerifier = sessionStorage.getItem(this.aconexCodeVerifier);
            if (authorizationCode && codeVerifier && this.clientId) {
                const aconexOauthResponse = await aconexApi.getToken(authorizationCode, codeVerifier);
                accessToken = aconexOauthResponse.access_token;

                // store aconex access and refresh tokens
                this.storeAccessToken(aconexOauthResponse);
                this.storeRefreshAccessToken(aconexOauthResponse);

                // remove code verifier from session storage
                sessionStorage.removeItem(this.aconexCodeVerifier);
                // remove state from local storage
                localStorage.removeItem(this.aconexStateCode);
            }
        } catch (error) {
            console.log(error);
        }

        return accessToken;
    };

    isAccessTokenValid = (accessToken: string | undefined): boolean => {
        let isValid: boolean = false;
        if (accessToken) {
            const tokenDecoded: any = jwt_decode(accessToken);
            const currentTimeInSeconds = Math.floor(Date.now() / 1000);
            isValid = currentTimeInSeconds < tokenDecoded.exp - 300;
        }

        return isValid;
    };

    private refreshAccessToken = async (): Promise<string | undefined> => {
        let accessToken: string | undefined;

        if (this.clientId) {
            const refreshToken = this.getCachedRefreshAccessToken();
            const aconexOauthResponse = await aconexApi.refreshToken(refreshToken!);

            // store access and refresh tokens in cookies
            this.storeAccessToken(aconexOauthResponse);
            this.storeRefreshAccessToken(aconexOauthResponse);

            accessToken = aconexOauthResponse.access_token;
        }

        return accessToken;
    };

    /**
     * Stores the aconex client id in a cookie.
     * @param aconexClientId the aconex client id
     */
    private storeClientId = (aconexClientId: string) => {
        cookieService.setCookie(this.aconexClientIdText, aconexClientId);
    };

    /**
     * Stores the access token in two cookies.
     * @param aconexOauthResponse the aconex oauth response
     */
    private storeAccessToken = (aconexOauthResponse: AconexOauthResponse) => {
        const accessToken = aconexOauthResponse.access_token;
        const accessTokenMiddle = Math.ceil(accessToken.length / 2);
        const accessTokenOne = accessToken.slice(0, accessTokenMiddle);
        const accessTokenTwo = accessToken.slice(accessTokenMiddle);

        cookieService.setCookie(this.aconexAccessTokenTextOne, accessTokenOne);
        cookieService.setCookie(this.aconexAccessTokenTextTwo, accessTokenTwo);
    };

    /**
     * Stores the refresh token in a cookie.
     * @param aconexOauthResponse the aconex oauth response
     */
    private storeRefreshAccessToken = (aconexOauthResponse: AconexOauthResponse) => {
        cookieService.setCookie(this.aconexAccessRefreshTokenText, aconexOauthResponse.refresh_token);
    };

    /**
     * Removes the client id cookie.
     */
    private removeClientId = () => {
        cookieService.removeCookie(this.aconexClientIdText);
    };

    /**
     * Removes the access token cookie.
     */
    private removeAccessToken = () => {
        cookieService.removeCookie(this.aconexAccessTokenTextOne);
        cookieService.removeCookie(this.aconexAccessTokenTextTwo);
    };

    /**
     * Removes the refresh token cookie.
     */
    private removeRefreshAccessToken = () => {
        cookieService.removeCookie(this.aconexAccessRefreshTokenText);
    };
}

const aconexService: AconexService = new AconexService();
export default aconexService;
